import { useMsal } from '@azure/msal-react';
import { InteractionRequiredAuthError } from '@azure/msal-browser';
import { apiTokenRequest } from '../auth/authConfig';
import { useEffect } from 'react';
import { getSignalRConnectionId } from '../signalr/signalRConnectionStore';

const HttpInterceptor = () => {
  const { instance, accounts } = useMsal();

  useEffect(() => {
    let hasLoggedTokenClaims = false;

    const logTokenClaims = (accessToken: string) => {
      if (hasLoggedTokenClaims) {
        return;
      }

      try {
        const payload = JSON.parse(atob(accessToken.split('.')[1]));
/*         console.log('[HttpInterceptor] Access token claims:', {
          aud: payload.aud,
          scp: payload.scp,
          iss: payload.iss,
          tid: payload.tid,
          azp: payload.azp,
          appid: payload.appid
        }); */
        hasLoggedTokenClaims = true;
      } catch (decodeError) {
        console.warn('[HttpInterceptor] Could not decode access token claims', decodeError);
      }
    };

    const originalFetch = window.fetch;

    window.fetch = async (input, init = {}) => {
      const url = typeof input === 'string' ? input : input instanceof Request ? input.url : '';
      const requestMethod = (init.method || (input instanceof Request ? input.method : 'GET')).toUpperCase();
      const isMutatingRequest = ['POST', 'PUT', 'DELETE'].includes(requestMethod);

      // Only exclude direct Azure SignalR service calls from auth/header interception.
      // Backend negotiate/join endpoints require Bearer auth.
      const isAzureSignalRServiceRequest = url.includes('.signalr.net');
      const isAuthRequest =
        url.includes('login.microsoftonline.com') ||
        url.includes('/oauth2/v2.0/token') ||
        url.includes('/oauth2/v2.0/authorize') ||
        url.includes('login.windows.net');
      
      if (!isAzureSignalRServiceRequest && !isAuthRequest && accounts.length > 0) {
        try {
          const request = {
            ...apiTokenRequest,
            account: accounts[0]
          };
          //console.log('[HttpInterceptor] Attempting to acquire token silently for:', url);
          const response = await instance.acquireTokenSilent(request);
          //console.log('[HttpInterceptor] ✅ Token acquired successfully');
          logTokenClaims(response.accessToken);
          
          const headers = new Headers(init.headers || {});
          headers.set('Authorization', `Bearer ${response.accessToken}`);
          init.headers = headers;
        } catch (error) {
          if (error instanceof InteractionRequiredAuthError) {
            //console.log('[HttpInterceptor] ⚠️ Interaction required - attempting interactive token acquisition');
            try {
              const request = {
                ...apiTokenRequest,
                account: accounts[0]
              };
              const response = await instance.acquireTokenPopup(request);
              //console.log('[HttpInterceptor] ✅ Interactive token acquired successfully');
              logTokenClaims(response.accessToken);
              
              const headers = new Headers(init.headers || {});
              headers.set('Authorization', `Bearer ${response.accessToken}`);
              init.headers = headers;
            } catch (popupError) {
              console.error('[HttpInterceptor] ❌ Interactive token acquisition failed:', popupError);
            }
          } else {
            console.error('[HttpInterceptor] ❌ Error acquiring token:', error);
          }
        }
      }

      if (!isAzureSignalRServiceRequest && isMutatingRequest) {
        const signalRConnectionId = getSignalRConnectionId();
        if (signalRConnectionId) {
          const headers = new Headers(init.headers || {});
          headers.set('X-SignalR-Connection-Id', signalRConnectionId);
          init.headers = headers;
          //console.log('[HttpInterceptor] Added X-SignalR-Connection-Id header:', signalRConnectionId);
        }
      }
      
      // Make the fetch request
      let fetchResponse = await originalFetch(input, init);
      
      // If we get a 401 and we have accounts, try to refresh and retry once
      if (fetchResponse.status === 401 && accounts.length > 0 && !isAzureSignalRServiceRequest && !isAuthRequest) {
        //console.log('[HttpInterceptor] 🔄 Received 401 - attempting token refresh and retry');
        try {
          const request = {
            ...apiTokenRequest,
            account: accounts[0],
            forceRefresh: true // Force a new token
          };
          const response = await instance.acquireTokenSilent(request);
          //console.log('[HttpInterceptor] ✅ Token refreshed after 401');
          logTokenClaims(response.accessToken);
          
          // Create a new request with the refreshed token
          const newInit = { ...init };
          const headers = new Headers(newInit.headers || {});
          headers.set('Authorization', `Bearer ${response.accessToken}`);

          if (!isAzureSignalRServiceRequest && isMutatingRequest) {
            const signalRConnectionId = getSignalRConnectionId();
            if (signalRConnectionId) {
              headers.set('X-SignalR-Connection-Id', signalRConnectionId);
            }
          }

          newInit.headers = headers;
          
          // Retry the original request
          //console.log('[HttpInterceptor] 🔄 Retrying request with refreshed token');
          fetchResponse = await originalFetch(input, newInit);
          //console.log('[HttpInterceptor] ✅ Retry successful, status:', fetchResponse.status);
        } catch (refreshError) {
          console.error('[HttpInterceptor] ❌ Token refresh after 401 failed:', refreshError);
          // Return the original 401 response
        }
      }
      
      return fetchResponse;
    };
    
    return () => {
      window.fetch = originalFetch;
    };
  }, [instance, accounts]);

  return null;
}

export default HttpInterceptor;